/**
 * Singleton typed socket.io-client.
 *
 * Typed with ClientToServerEvents / ServerToClientEvents from @syncreel/shared.
 * Handshake auth carries { token, roomId }; the server middleware verifies the
 * JWT and room membership before any handler runs (§4.4).
 *
 * WS-8's useRoomSync and WS-5's RoomPage both consume this singleton so there
 * is exactly one socket per room per tab.
 */
import { io, type Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '@syncreel/shared';
import { useSessionStore } from '../store/session';
import { logger } from '../api/logger';

/** Typed socket: listens to ServerToClientEvents, emits ClientToServerEvents. */
export type SyncSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let current: SyncSocket | null = null;
let currentRoomId: string | null = null;

/**
 * Get (or create) the singleton socket for `roomId`. If a socket for a
 * different room exists, it is disconnected and replaced. Throws if no
 * session token is present — callers must ensure a session first.
 */
export function getSocket(roomId: string): SyncSocket {
  const token = useSessionStore.getState().token;
  if (!token) {
    // WHY: the server rejects handshakes without a valid token; fail fast
    // with a clear error rather than letting the socket dangle on E_UNAUTHORIZED.
    throw new Error('Cannot open socket without a session token.');
  }

  if (current && currentRoomId === roomId) {
    // WHY: return the existing socket even if it's still connecting —
    // recreating it drops listeners that other components (e.g. RoomStage)
    // already attached, causing them to miss room:state/room:members events.
    return current;
  }

  // Replace any existing socket bound to a different (or stale) room.
  if (current) {
    current.removeAllListeners();
    current.disconnect();
    current = null;
    currentRoomId = null;
  }

  // WHY: same-origin in dev (Vite proxy) and prod (nginx) — no transport config
  // needed; socket.io-client defaults to relative path /socket.io. The installed
  // socket.io-client version does not accept type arguments on io(), so cast the
  // result to SyncSocket to pin the event maps so emit/on are fully typed.
  const sock = io({
    auth: { token, roomId },
    transports: ['websocket', 'polling'],
  }) as SyncSocket;

  sock.on('connect_error', (err) => {
    logger.warn('socket connect error', err.message);
  });
  sock.on('disconnect', (reason) => {
    logger.debug('socket disconnected', reason);
  });

  current = sock;
  currentRoomId = roomId;
  return sock;
}

/** Return the active socket if any (without creating one). */
export function getActiveSocket(): SyncSocket | null {
  return current;
}

/** Disconnect and drop the singleton. Called on room teardown/logout. */
export function disconnectSocket(): void {
  if (current) {
    current.removeAllListeners();
    current.disconnect();
    current = null;
    currentRoomId = null;
  }
}
